import React, { useState } from 'react'
import type {
  TextFieldProps as AriaTextFieldProps,
  InputProps,
  ValidationResult,
} from 'react-aria-components'
import { TextField as AriaTextField } from 'react-aria-components'
import { Description, FieldError, Input, Label, fieldBorderStyles } from './Field'

import { composeTailwindRenderProps, focusRing } from '~/design-system/helpers'
import tv from '~/lib/tv'
import type { VariantProps } from '~/lib/tv'

const textFieldVariants = tv({
  extend: focusRing,
  base: 'border rounded [text-align:inherit]',
  variants: {
    isFocused: fieldBorderStyles.variants.isFocusWithin,
    isInvalid: fieldBorderStyles.variants.isInvalid,
    isDisabled: fieldBorderStyles.variants.isDisabled,
    aparence: {
      default: '',
      textfield:
        '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
    },
  },
})

export interface TextFieldProps extends AriaTextFieldProps {
  label?: string | React.ReactNode
  description?: string
  errorMessage?: string | ((validation: ValidationResult) => string)
  inputVariants?: VariantProps<typeof textFieldVariants>
  errorProps?: React.HTMLAttributes<HTMLSpanElement>
}

interface Props extends TextFieldProps {
  labelClassName?: HTMLLabelElement['className']
  inputClassName?: InputProps['className']
  inputProps?: InputProps
  placeholder?: string
}

export default function TextField({
  label,
  description,
  errorMessage,
  inputVariants,
  inputClassName = '',
  labelClassName,
  errorProps = {},
  inputProps,
  ...props
}: Props) {
  const [isFocused, setIsFocused] = useState(false)

  const handleFocus = () => setIsFocused(true)
  const handleBlur = (event: { target: { checkValidity: () => any } }) => {
    setIsFocused(false)
  }

  return (
    <AriaTextField {...props} className={composeTailwindRenderProps(props.className, 'flex flex-col gap-1')}>
      {label && <Label className={labelClassName}>{label}</Label>}
      <Input
        className={`max-h-9 ${textFieldVariants({
          isFocused,
          isDisabled: props.isDisabled,
          ...inputVariants,
        })} ${inputClassName}`}
        onBlur={handleBlur}
        onFocus={handleFocus}
        {...inputProps}
      />
      {description && <Description>{description}</Description>}
      <FieldError {...errorProps}>{errorMessage}</FieldError>
    </AriaTextField>
  )
}
